import { useState } from "react";
import {
  usePushSaleToReach,
  getListSalesQueryKey,
  getGetSalesSyncStatusQueryKey,
  type ReachSalePayload,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Loader2, Info } from "lucide-react";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/components/inventory-cells";

function fieldRows(p: ReachSalePayload): { label: string; value: string }[] {
  const seats = p.seats.length > 0 ? p.seats.join(", ") : "—";
  return [
    { label: "Order / listing #", value: p.listingNumber ?? "—" },
    { label: "Event", value: p.eventName ?? "—" },
    { label: "Date", value: p.eventDate ?? "—" },
    { label: "Venue", value: p.venue ?? "—" },
    { label: "Section", value: p.section ?? "—" },
    { label: "Row", value: p.row ?? "—" },
    { label: "Seats", value: seats },
    { label: "Quantity", value: p.quantity != null ? String(p.quantity) : "—" },
    {
      label: "Payout / ticket",
      value: formatMoney(p.payoutPerTicket, p.currency),
    },
    { label: "Total payout", value: formatMoney(p.totalPayout, p.currency) },
    { label: "Currency", value: p.currency ?? "—" },
    { label: "Account email", value: p.vendorAccountEmail ?? "—" },
  ];
}

function PayloadTable({ payload }: { payload: ReachSalePayload }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
      {fieldRows(payload).map((r) => (
        <div key={r.label} className="contents">
          <dt className="text-muted-foreground whitespace-nowrap">{r.label}</dt>
          <dd className="text-right font-medium text-foreground break-all">
            {r.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * "Push to Reach" action for a sale line that isn't yet recorded in Reach.
 * - Hover/focus shows the exact payload that would be sent.
 * - Clicking opens a confirm dialog re-showing the payload; the push only fires
 *   on the explicit "Confirm & push" button (never a single accidental click).
 * - While the Reach create-sale endpoint is not configured, the confirm button
 *   is disabled with a "coming soon" note; the preview/confirm flow stays usable.
 */
export function PushToReachAction({
  payload,
  pushConfigured,
}: {
  payload: ReachSalePayload;
  pushConfigured: boolean;
}) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const push = usePushSaleToReach({
    mutation: {
      onSuccess: (result) => {
        if (result.verified) {
          toast.success("Sale pushed to Reach and verified.");
          // Only flip the line to "In Reach" after a verified write.
          qc.invalidateQueries({ queryKey: getListSalesQueryKey() });
          setOpen(false);
        } else if (!result.configured) {
          toast.message(result.message);
        } else {
          // Pushed but not verified — surface the message, keep the dialog open.
          toast.error(result.message);
          qc.invalidateQueries({ queryKey: getListSalesQueryKey() });
        }
      },
      onError: () => toast.error("Push to Reach failed."),
    },
  });

  const confirmPush = () => {
    push.mutate({ saleEmailId: payload.saleEmailId });
  };

  return (
    <>
      <HoverCard openDelay={120} closeDelay={80}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1 rounded-sm border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[11px] font-medium text-amber-300 hover:bg-amber-400/20 focus:outline-none focus:ring-1 focus:ring-amber-400/60"
          >
            <Upload className="h-3 w-3" />
            Push to Reach
          </button>
        </HoverCardTrigger>
        <HoverCardContent align="end" className="w-72">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-foreground">
            <Info className="h-3.5 w-3.5 text-amber-400" />
            Payload preview
          </div>
          <PayloadTable payload={payload} />
        </HoverCardContent>
      </HoverCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Push sale to Reach</DialogTitle>
            <DialogDescription>
              Review the exact data that will be sent to Reach. This pushes the
              sale exactly as parsed — nothing here can be edited.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-border bg-muted/30 p-3">
            <PayloadTable payload={payload} />
          </div>
          {!pushConfigured && (
            <p className="rounded-sm bg-amber-400/10 px-3 py-2 text-[11px] text-amber-300">
              Functionality coming soon — push API not configured yet.
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={push.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={confirmPush}
              disabled={!pushConfigured || push.isPending}
              title={
                pushConfigured
                  ? undefined
                  : "Functionality coming soon — push API not configured yet."
              }
            >
              {push.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              Confirm &amp; push
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Compact "Push" button for the dense Sales table. Unlike PushToReachAction it
 * has no payload preview or confirm modal — the push fires immediately on click
 * (the server rebuilds the exact payload from the sale email), shows a loading
 * spinner, and refreshes the affected row on success. Disabled with a "coming
 * soon" note while the Reach create-sale endpoint is not configured.
 */
export function PushToReachButton({
  saleEmailId,
  pushConfigured,
}: {
  saleEmailId: number;
  pushConfigured: boolean;
}) {
  const qc = useQueryClient();
  const push = usePushSaleToReach({
    mutation: {
      onSuccess: (result) => {
        if (result.verified) {
          toast.success("Sale pushed to Reach and verified.");
          qc.invalidateQueries({ queryKey: getListSalesQueryKey() });
          qc.invalidateQueries({ queryKey: getGetSalesSyncStatusQueryKey() });
        } else if (!result.configured) {
          toast.message(result.message);
        } else {
          toast.error(result.message);
          qc.invalidateQueries({ queryKey: getListSalesQueryKey() });
        }
      },
      onError: () => toast.error("Push to Reach failed."),
    },
  });

  return (
    <button
      type="button"
      onClick={() => push.mutate({ saleEmailId })}
      disabled={!pushConfigured || push.isPending}
      title={
        pushConfigured
          ? "Push this sale to Reach"
          : "Functionality coming soon — push API not configured yet."
      }
      className="inline-flex items-center gap-1 rounded-sm border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[11px] font-medium text-amber-300 hover:bg-amber-400/20 focus:outline-none focus:ring-1 focus:ring-amber-400/60 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {push.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Upload className="h-3 w-3" />
      )}
      Push
    </button>
  );
}
